<script setup>
import {ref, watch, computed, defineProps, defineEmits, onMounted} from 'vue';
import countries from 'i18n-iso-countries';
import { postcodeValidator } from 'postcode-validator';
import 'element-plus/dist/index.css';
import { useI18n } from "vue-i18n";

// Register locales
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
countries.registerLocale(require("i18n-iso-countries/langs/el.json")); 

const props = defineProps({
  modelValue: String,      // postal code
  countryValue: String     // selected country
});

// Emits for v-model
const emit = defineEmits(['update:modelValue', 'update:countryValue', 'update:invalidPostCode']);

const { locale, t } = useI18n();

// Internal state
const postalCode = ref(props.modelValue || '');
const country = ref(props.countryValue || 'GR');
const invalidPostCode = ref(false);

// Computed countries list based on current language
const countriesList = computed(() => {
  const lang = locale.value;
  return Object.entries(countries.getNames(lang)).map(([code, name]) => ({
    code,
    name
  }));
});


onMounted(() => {
  checkPostal();
});

// Watch internal postalCode and emit updates
watch(postalCode, (val) => {
  emit('update:modelValue', val);
  checkPostal();
});

// Watch internal country and emit updates
watch(country, () => {
  postalCode.value = '';
  checkPostal();
  emit('update:countryValue', country.value);
});

// Check postal code validity
const checkPostal = () => {
  if (!postalCode.value) {
    invalidPostCode.value = false;
    emit('update:invalidPostCode', false);
    return;
  }

  try {
    const valid = postcodeValidator(postalCode.value, country.value);
    invalidPostCode.value = !valid;
    emit('update:invalidPostCode', !valid);
  } catch (e) {
    // unsupported country -> no validation
    invalidPostCode.value = false;
    emit('update:invalidPostCode', false);
  }
};
</script>

<template>
  <div class="w-full max-w-md">
    <div class="flex gap-2 mt-1">
      <!-- Country Dropdown -->
      <el-select
          v-model="country"
          placeholder="Country"
          filterable
          class="w-1/2"
      >
        <el-option
            v-for="c in countriesList"
            :key="c.code"
            :label="c.name"
            :value="c.code"
        />
      </el-select>

      <!-- Postal Code Input -->
      <el-input
          v-model="postalCode"
          :placeholder="t('howToReturnPage.zipCode')"
          class="flex-1"
      />
    </div>
  </div>
</template>


<style scoped>
:deep(.el-input__wrapper),
:deep(.el-select .el-input__wrapper) {
  border-radius: 0 !important;
}

</style>